import React from 'react';
import { Box, Divider, Stack, TextField, Typography, alpha, useTheme } from '@mui/material';
import { Work as UseItIcon } from '@mui/icons-material';

const StoryStep = ({ config, response = {}, onChange, disabled }) => {
  const theme = useTheme();

  return (
    <Stack spacing={2.5}>
      {/* Banner */}
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5,
        px: 1.5, py: 1.25,
        bgcolor: alpha(theme.palette.warning.main, 0.07),
        borderLeft: `3px solid ${theme.palette.warning.main}`,
        borderRadius: '0 6px 6px 0',
      }}>
        <UseItIcon sx={{ fontSize: 18, color: 'warning.main', mt: 0.15, flexShrink: 0 }} />
        <Box>
          <Typography variant="body2" fontWeight={700} color="warning.main">
            Apply it in a real job context
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
            Imagine you are in a real role. Your manager asks a question. How does this concept help you answer it?
            Write as if you are actually on the job — specific role, specific question, specific answer.
          </Typography>
        </Box>
      </Box>

      {/* Job role */}
      <Box>
        <Typography variant="body2" fontWeight={700} mb={0.5}>Your role</Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
          What job are you in? (e.g. Data Analyst at an e-commerce company, Reporting Analyst at a bank)
        </Typography>
        <TextField
          value={response.role || ''}
          onChange={(e) => onChange({ ...response, role: e.target.value })}
          placeholder="e.g. Junior Data Analyst at a retail company…"
          fullWidth
          disabled={disabled}
          size="small"
        />
      </Box>

      <Divider />

      {/* The question from the manager */}
      <Box>
        <Typography variant="body2" fontWeight={700} mb={0.5}>
          The question your manager asks
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
          What real business question lands on your desk? Make it specific — not "analyse the data" but "which product categories drove the most revenue last month?"
        </Typography>
        <TextField
          value={response.problem || ''}
          onChange={(e) => onChange({ ...response, problem: e.target.value })}
          placeholder="e.g. 'Can you find out which product categories exceeded £5,000 in sales this quarter? We need it before the 9am standup.'"
          multiline
          minRows={2}
          fullWidth
          disabled={disabled}
          size="small"
        />
      </Box>

      {/* How you solve it */}
      <Box>
        <Typography variant="body2" fontWeight={700} mb={0.5}>
          How you answer it — step by step
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
          Describe what you do: which table you query, which clauses you use, and what the result looks like. Write it as a narrative, not code.
        </Typography>
        <TextField
          value={response.story || ''}
          onChange={(e) => onChange({ ...response, story: e.target.value })}
          placeholder={
            'e.g. I open the orders table in our database tool. I write a query that groups all orders by product_category, ' +
            'sums the order_value for each group, then filters with HAVING to keep only categories where the total exceeds £5,000. ' +
            'I order the result from highest to lowest. Three minutes later I have the answer ready to share in Slack.'
          }
          multiline
          minRows={5}
          fullWidth
          disabled={disabled}
          size="small"
        />
      </Box>
    </Stack>
  );
};

export default StoryStep;
